/**
 * ResolveConflictDialog — confirm and add resolution note.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/contexts/ToastContext';
import { useResolveConflict } from '../api';
import type { BuddyGroupConflict } from '../types/conflict';

interface ResolveConflictDialogProps {
  conflict: BuddyGroupConflict;
  action: 'dismiss' | 'resolve';
  open: boolean;
  onClose: () => void;
}

export function ResolveConflictDialog({ conflict, action, open, onClose }: ResolveConflictDialogProps) {
  const { showSuccess, showError } = useToast();
  const [note, setNote] = useState('');
  const mutation = useResolveConflict();

  const handleConfirm = async () => {
    try {
      await mutation.mutateAsync({
        conflictId: conflict.id,
        payload: {
          action,
          resolution_note: note.trim() || undefined,
        },
      });
      showSuccess(action === 'dismiss' ? 'Conflict dismissed' : 'Conflict resolved');
      onClose();
    } catch (err: any) {
      showError('Failed', err?.message ?? 'An error occurred.');
    }
  };

  const actionLabel = action === 'dismiss' ? 'Dismiss' : 'Resolve';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{actionLabel} Conflict</DialogTitle>
          <DialogDescription>
            {action === 'dismiss'
              ? 'Dismissing this conflict will mark it as not requiring action.'
              : 'Resolving this conflict indicates the issue has been addressed.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="resolution-note">
            Note (optional)
          </label>
          <textarea
            id="resolution-note"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 min-h-[80px]"
            placeholder="Add a note about this resolution..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={mutation.isPending}>
            {mutation.isPending ? `${actionLabel}ing...` : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
